import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
// Ui
import Text from 'ui3/Text/Text';
// Styles
import styles from './OTPInput.module.scss';

type AllowedInputTypes = 'password' | 'text' | 'number' | 'tel';

type OTPInputProps = {
  /** Value of the OTP input */
  value: string;
  /** Number of OTP inputs to be rendered */
  numInputs?: number;
  /** Callback to be called when the OTP value changes */
  onChange: (otp: string) => void;
  /** Whether the input is in error state */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Whether the first input should be auto focused */
  shouldAutoFocus?: boolean;
  /** The type that will be passed to the input being rendered */
  inputType?: AllowedInputTypes;
  /** For react-hook-form integration */
  name?: string;
  /** For react-hook-form integration */
  onBlur?: () => void;
};

const OTPInput = forwardRef<HTMLInputElement, OTPInputProps>(
  (
    {
      value = '',
      numInputs = 5,
      onChange,
      error = false,
      errorMessage = 'Wrong code, please try again.',
      shouldAutoFocus = false,
      inputType = 'text',
      name,
      onBlur: fieldOnBlur,
    },
    ref
  ) => {
    const [activeInput, setActiveInput] = useState(0);
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    // Expose methods and elements to parent component via ref
    useImperativeHandle(
      ref,
      () =>
        (({
          focus: () => {
            inputRefs.current[0]?.focus();
          },
          blur: () => {
            inputRefs.current[activeInput]?.blur();
          },
          // Forward important properties to act like a regular input
          name,
          value,
        } as unknown) as HTMLInputElement)
    );

    const getOTPValue = () => (value ? value.toString().split('') : []);

    const isInputNum = inputType === 'number' || inputType === 'tel';
    const isAllFieldsFilled = value.length === numInputs;

    useEffect(() => {
      inputRefs.current = inputRefs.current.slice(0, numInputs);
    }, [numInputs]);

    useEffect(() => {
      if (shouldAutoFocus) {
        inputRefs.current[0]?.focus();
      }
    }, [shouldAutoFocus]);

    const isInputValueValid = (value: string) => {
      const isTypeValid = isInputNum
        ? !isNaN(Number(value))
        : typeof value === 'string';
      return isTypeValid && value.trim().length === 1;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (isInputValueValid(value)) {
        changeCodeAtFocus(value);
        focusInput(activeInput + 1);
      }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (!isInputValueValid(value)) {
        if (value.length === numInputs) {
          const hasInvalidInput = value
            .split('')
            .some((cellInput) => !isInputValueValid(cellInput));
          if (!hasInvalidInput) {
            handleOTPChange(value.split(''));
            focusInput(numInputs - 1);
          }
        }

        // Handle backspace for various browsers
        // InputEvent is not directly typed in React's types
        const nativeInputType = (event.nativeEvent as any).inputType;
        if (nativeInputType === 'deleteContentBackward') {
          event.preventDefault();
          changeCodeAtFocus('');
          focusInput(activeInput - 1);
        }

        event.target.value = '';
      }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => (
      index: number
    ) => {
      setActiveInput(index);
      event.target.select();
    };

    const handleBlur = () => {
      // Call the onBlur provided by react-hook-form if available
      if (fieldOnBlur) {
        fieldOnBlur();
      }
      setActiveInput(activeInput - 1);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const otp = getOTPValue();
      if ([event.code, event.key].includes('Backspace')) {
        event.preventDefault();
        changeCodeAtFocus('');
        focusInput(activeInput - 1);
      } else if (event.code === 'Delete') {
        event.preventDefault();
        changeCodeAtFocus('');
      } else if (event.code === 'ArrowLeft') {
        event.preventDefault();
        focusInput(activeInput - 1);
      } else if (event.code === 'ArrowRight') {
        event.preventDefault();
        focusInput(activeInput + 1);
      }
      // React does not trigger onChange when the same value is entered again
      else if (event.key === otp[activeInput]) {
        event.preventDefault();
        focusInput(activeInput + 1);
      } else if (
        event.code === 'Spacebar' ||
        event.code === 'Space' ||
        event.code === 'ArrowUp' ||
        event.code === 'ArrowDown'
      ) {
        event.preventDefault();
      }
    };

    const focusInput = (index: number) => {
      const activeInput = Math.max(Math.min(numInputs - 1, index), 0);

      if (inputRefs.current[activeInput]) {
        inputRefs.current[activeInput]?.focus();
        inputRefs.current[activeInput]?.select();
        setActiveInput(activeInput);
      }
    };

    const changeCodeAtFocus = (value: string) => {
      const otp = getOTPValue();
      otp[activeInput] = value[0];
      handleOTPChange(otp);
    };

    const handleOTPChange = (otp: Array<string>) => {
      const otpValue = otp.join('');
      onChange?.(otpValue);
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();

      const otp = getOTPValue();
      let nextActiveInput = activeInput;

      // Get pastedData in an array of max size (num of inputs - current position)
      const pastedData = event.clipboardData
        .getData('text/plain')
        .slice(0, numInputs - activeInput)
        .split('');

      // Prevent pasting if the clipboard data contains non-numeric values for number inputs
      if (isInputNum && pastedData.some((value) => isNaN(Number(value)))) {
        return;
      }

      // Paste data from focused input onwards
      for (let pos = 0; pos < numInputs; ++pos) {
        if (pos >= activeInput && pastedData.length > 0) {
          otp[pos] = pastedData.shift() ?? '';
          nextActiveInput++;
        }
      }

      focusInput(nextActiveInput);
      handleOTPChange(otp);
    };

    return (
      <div className={styles.otpContainer} ref={containerRef}>
        {/* Hidden input for react-hook-form to attach to */}
        {name && (
          <input
            type="hidden"
            name={name}
            value={value}
            ref={ref as React.RefObject<HTMLInputElement>}
          />
        )}
        <div
          className={`${styles.otpInputContainer} ${
            error ? styles.error : ''
          } ${isAllFieldsFilled && !error ? styles.filled : ''}`}
        >
          {Array.from({ length: numInputs }, (_, index) => index).map(
            (index) => (
              <input
                key={index}
                type={inputType}
                inputMode={isInputNum ? 'numeric' : 'text'}
                value={getOTPValue()[index] ?? ''}
                ref={(element) => (inputRefs.current[index] = element)}
                onChange={handleChange}
                onFocus={(event) => handleFocus(event)(index)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onInput={handleInputChange}
                className={styles.otpInput}
                autoComplete="off"
                aria-label={`Please enter OTP character ${index + 1}`}
              />
            )
          )}
        </div>
        <div className={styles.errorContainer}>
          {error && errorMessage && (
            <Text
              variant="body2Regular"
              color="error-default"
              className={styles.errorMessage}
            >
              {errorMessage}
            </Text>
          )}
        </div>
      </div>
    );
  }
);

OTPInput.displayName = 'OTPInput';

export default OTPInput;
